// PhotoLightboxWithInfo.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import ThumbnailImage from "../components/ThumbnailImage";
import { api } from "../api";
import { useLanguage } from "../hooks/useLanguage";

const copy = {
  RU: {
    photo: "Фото",
    expand: "Развернуть",
    collapse: "Свернуть",
    showCarousel: "Показать карусель",
    hideCarousel: "Скрыть карусель",
    openPhoto: "Открыть информацию о фото",
    products: "Изделия на фото:",
    details: "Детали на фото:",
    productFallback: "Изделие",
    detailFallback: "Деталь",
    loading: "Загрузка связей…",
  },
  EN: {
    photo: "Photo",
    expand: "Expand",
    collapse: "Collapse",
    showCarousel: "Show thumbnails",
    hideCarousel: "Hide thumbnails",
    openPhoto: "Open photo information",
    products: "Examples in this photo:",
    details: "Parts in this photo:",
    productFallback: "Example",
    detailFallback: "Part",
    loading: "Loading linked records…",
  },
};

/** -------------------- localStorage helpers -------------------- **/
const LS_KEYS = {
  infoOpen: "photoLightbox:infoOpen",
  showThumbs: "photoLightbox:showThumbs",
};

function readBool(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return raw === "true";
  } catch {
    return fallback;
  }
}

function writeBool(key, value) {
  try {
    localStorage.setItem(key, value ? "true" : "false");
  } catch {
    // ignore
  }
}

/** -------------------- Мини-карусель -------------------- **/
const MiniThumbBar = React.memo(function MiniThumbBar({ slides, currentIndex, onSelect, labels }) {
  const wrapRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const item = wrap.querySelector(`[data-thumb-idx="${currentIndex}"]`);
    if (!item) return;

    const wrapRect = wrap.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const overLeft = itemRect.left < wrapRect.left;
    const overRight = itemRect.right > wrapRect.right;

    if (overLeft || overRight) {
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const targetScrollLeft = Math.max(0, itemCenter - wrap.clientWidth / 2);
      wrap.scrollTo({ left: targetScrollLeft, behavior: initializedRef.current ? "smooth" : "auto" });
    }
    if (!initializedRef.current) initializedRef.current = true;
  }, [currentIndex]);

  if (!slides?.length) return null;

  return (
    <div className="photo-lightbox-thumbs p-2 w-full sm:w-auto max-w-[100vw] sm:max-w-[50vw]">
      <div
        ref={wrapRef}
        className="flex gap-2 overflow-x-auto whitespace-nowrap scroll-px-4"
        style={{ scrollbarWidth: "thin", overscrollBehaviorX: "contain" }}
      >
        {slides.map((s, i) => (
          <button
            key={s.id ?? `i${i}`}
            data-thumb-idx={i}
            onClick={() => onSelect(i)}
            title={`${labels.photo} ${s.id ?? i + 1}`}
            className={[
              "photo-lightbox-thumb relative shrink-0 overflow-hidden border bg-transparent p-0",
              "outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
              i === currentIndex ? "is-active" : "",
            ].join(" ")}
            style={{
              width: 84,
              height: 56,
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <ThumbnailImage
              src={s.thumb ?? s.src}
              alt={s.alt ?? `${labels.photo} ${s.id ?? i + 1}`}
              className="w-full h-full object-cover select-none"
              draggable={false}
              decoding="sync"
            />
            {i === currentIndex && <span className="photo-lightbox-thumb__marker absolute inset-0 pointer-events-none" />}
          </button>
        ))}
      </div>
    </div>
  );
});

/** -------------------- Инфо-окно -------------------- **/
function InfoCard({
  slide,
  meta,
  isOpen,
  onToggle,
  canShowThumbs,
  showThumbs,
  onToggleThumbs,
  onNavigate,
  onAfterAction, // ✅ вернуть фокус в Lightbox
  labels,
}) {
  const id = slide?.id;
  const hasProducts = !!(meta?.products?.length);
  const hasDetails = !!(meta?.details?.length);

  if (!id) return <div className="w-full sm:w-auto" />;

  if (!isOpen) {
    return (
      <div className="flex w-full justify-end sm:block sm:w-auto">
        <button
          onClick={() => {
            onToggle?.();
            onAfterAction?.();
          }}
          type="button"
          className="photo-lightbox-collapsed px-3 py-2 text-xs transition"
          aria-label={labels.expand}
          title={labels.expand}
        >
          {labels.expand}
        </button>
      </div>
    );
  }

  return (
    <div className="photo-lightbox-info w-full sm:w-auto max-w-[100vw] sm:max-w-[42vw]">
      <div className="photo-lightbox-info__header flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 py-2 border-b">
        <div className="font-medium text-sm">{labels.photo} №{id}</div>
        <div className="flex items-center gap-2">
          {canShowThumbs && (
            <button
              type="button"
              onClick={() => {
                onToggleThumbs?.();
                onAfterAction?.();
              }}
              className="photo-lightbox-control px-3 py-2 text-xs transition whitespace-nowrap min-w-[8.5rem] text-center"
              aria-pressed={showThumbs}
              title={showThumbs ? labels.hideCarousel : labels.showCarousel}
            >
              {showThumbs ? labels.hideCarousel : labels.showCarousel}
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              onToggle?.();
              onAfterAction?.();
            }}
            className="photo-lightbox-control px-3 py-2 text-xs transition"
            aria-label={labels.collapse}
            title={labels.collapse}
          >
            {labels.collapse}
          </button>
        </div>
      </div>

      <div className="p-3 sm:p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/photos/${id}`}
            onClick={() => onNavigate?.()}
            className="photo-lightbox-primary inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold transition"
          >
            {labels.openPhoto}
          </Link>
        </div>

        {!!hasProducts && (
          <div className="photo-lightbox-info__panel px-3 py-2 border">
            <div className="photo-lightbox-info__label text-xs uppercase tracking-wide mb-1">{labels.products}</div>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {meta.products.map((prod) => (
                <Link
                  key={prod.id}
                  to={`/products/${prod.id}`}
                  onClick={() => onNavigate?.()}
                  className="photo-lightbox-info__link text-sm underline underline-offset-2"
                >
                  {prod.name || `${labels.productFallback} #${prod.id}`}
                </Link>
              ))}
            </div>
          </div>
        )}

        {!!hasDetails && (
          <div className="photo-lightbox-info__panel px-3 py-2 border">
            <div className="photo-lightbox-info__label text-xs uppercase tracking-wide mb-1">{labels.details}</div>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {meta.details.map((det) => {
                const typeOrName = det.type?.name?.trim?.() || det.name || `${labels.detailFallback} #${det.id}`;
                const label = det.serialNumber?.trim?.() ? `№${det.serialNumber} — ${typeOrName}` : typeOrName;

                return (
                  <Link
                    key={det.id}
                    onClick={() => onNavigate?.()}
                    to={`/details/${det.id}`}
                    className="photo-lightbox-info__link text-sm underline underline-offset-2"
                  >
                    {label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {meta === undefined && (
        <div className="photo-lightbox-info__loading border-t px-3 sm:px-4 py-2 text-xs">{labels.loading}</div>
      )}
    </div>
  );
}

export default function PhotoLightboxWithInfo({ photos, open, index, onClose, onIndexChange }) {
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const [infoOpen, setInfoOpen] = useState(() => readBool(LS_KEYS.infoOpen, true));
  const [showThumbs, setShowThumbs] = useState(() => readBool(LS_KEYS.showThumbs, true));
  const [photoMeta, setPhotoMeta] = useState({}); // { [id]: { products:[], details:[] } }

  const uploadHost = process.env.NODE_ENV === "development" ? "http://localhost:8080" : "";

  const slides = useMemo(() => {
    return (photos ?? []).map((p) => {
      const src = p.url?.startsWith("/") ? `${uploadHost}${p.url}` : p.url;
      const thumb = p.thumbnailUrl
        ? p.thumbnailUrl.startsWith("/")
          ? `${uploadHost}${p.thumbnailUrl}`
          : p.thumbnailUrl
        : src;
      return { src, alt: `${labels.photo} ${p.id}`, id: p.id, thumb };
    });
  }, [photos, uploadHost, labels.photo]);

  const canShowThumbs = slides.length > 1;

  useEffect(() => writeBool(LS_KEYS.infoOpen, infoOpen), [infoOpen]);
  useEffect(() => writeBool(LS_KEYS.showThumbs, showThumbs), [showThumbs]);

  useEffect(() => {
    if (!canShowThumbs) {
      setShowThumbs(false);
      return;
    }
    setShowThumbs(readBool(LS_KEYS.showThumbs, true));
  }, [canShowThumbs]);

  /** ✅ refs чтобы не было гонок и повторных запросов */
  const loadedRef = useRef(new Set()); // photoId которые уже загружали
  const inFlightRef = useRef(new Map()); // photoId -> Promise
  const openRef = useRef(false);

  useEffect(() => {
    openRef.current = !!open;
  }, [open]);

  /** ✅ фокус-таргет: всегда возвращаем фокус в лайтбокс, чтобы стрелки работали */
  const focusTargetRef = useRef(null);
  const refocusLightbox = useCallback(() => {
    // после клика меняется DOM (свернуть/развернуть, скрыть/показать),
    // фокус может "упасть" — возвращаем его внутрь лайтбокса
    requestAnimationFrame(() => {
      focusTargetRef.current?.focus?.({ preventScroll: true });
    });
  }, []);

  useEffect(() => {
    if (open) refocusLightbox();
  }, [open, refocusLightbox]);

  /** ✅ ensurePhotoMeta больше НЕ зависит от photoMeta */
  const ensurePhotoMeta = useCallback(async (photoId) => {
    if (photoId == null) return;
    if (loadedRef.current.has(photoId)) return;

    const existing = inFlightRef.current.get(photoId);
    if (existing) return existing;

    const p = (async () => {
      try {
        const { data } = await api.get(`/photos/${photoId}`);

        if (!openRef.current) return;

        setPhotoMeta((prev) => ({
          ...prev,
          [photoId]: { products: data.products || [], details: data.details || [] },
        }));
      } catch (e) {
        console.error(e);
        if (!openRef.current) return;

        setPhotoMeta((prev) => ({
          ...prev,
          [photoId]: { products: [], details: [] },
        }));
      } finally {
        loadedRef.current.add(photoId);
        inFlightRef.current.delete(photoId);
      }
    })();

    inFlightRef.current.set(photoId, p);
    return p;
  }, []);

  /** ✅ предзагрузка меты для текущего + соседних (чтобы меню не дергалось) */
  const prefetchAround = useCallback(
    (centerIndex) => {
      if (!openRef.current) return;
      if (!slides?.length) return;

      const ids = new Set();

      const addByIndex = (i) => {
        if (i < 0 || i >= slides.length) return;
        const id = slides[i]?.id;
        if (id != null) ids.add(id);
      };

      addByIndex(centerIndex);
      addByIndex(centerIndex - 1);
      addByIndex(centerIndex + 1);

      // В фоне, без await
      ids.forEach((id) => ensurePhotoMeta(id));
    },
    [slides, ensurePhotoMeta]
  );

  /** ✅ при открытии и смене индекса — префетчим текущий + соседей */
  useEffect(() => {
    if (!open) return;
    prefetchAround(index);
  }, [open, index, prefetchAround]);

  /** ✅ ВАЖНО: индекс меняем СРАЗУ, без await */
  const handleSelectThumb = useCallback(
    (i) => {
      onIndexChange?.(i); // мгновенно переключили
      prefetchAround(i); // ✅ текущая + соседи — фоном
      refocusLightbox(); // ✅ вернуть фокус внутрь лайтбокса
    },
    [prefetchAround, onIndexChange, refocusLightbox]
  );

  const handleToggleInfo = useCallback(() => setInfoOpen((v) => !v), []);
  const handleToggleThumbs = useCallback(() => {
    if (!canShowThumbs) return;
    setShowThumbs((v) => !v);
  }, [canShowThumbs]);

  return (
    <Lightbox
      open={!!open}
      close={onClose}
      index={index}
      slides={slides}
      plugins={[Zoom]}
      controller={{ closeOnBackdropClick: true }}
      zoom={{
          maxZoomPixelRatio: 2,      // было 1 по умолчанию — поставь 2..6 (или выше, но обычно хватает)
          zoomInMultiplier: 2.5,     // шаг приближения по кнопке/двойному клику
          doubleClickMaxStops: 2,    // больше “ступеней” приближения по double click / double tap
          scrollToZoom: true,        // опционально: зум колёсиком/трекпадом без Ctrl
          // minZoom: 1,             // при желании можно ограничить минимальный
        }}
      on={{
        view: ({ index: nextIndex }) => {
          onIndexChange?.(nextIndex); // мгновенно
          prefetchAround(nextIndex); // ✅ текущая + соседи — фоном
          refocusLightbox(); // ✅ стрелки продолжают работать
        },
      }}
      render={{
        buttonPrev: slides.length <= 1 ? () => null : undefined,
        buttonNext: slides.length <= 1 ? () => null : undefined,

        controls: () => {
          const slide = slides[index];
          const meta = slide?.id !== undefined ? photoMeta[slide.id] : undefined;

          return (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 px-3 sm:px-4 pb-[calc(env(safe-area-inset-bottom,0)+12px)]">
              {/* ✅ фокус-таргет внутри лайтбокса, чтобы ловить клавиши */}
              <div
                ref={focusTargetRef}
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                }}
              />

              <div className="pointer-events-auto flex flex-col sm:flex-row items-stretch sm:items-end gap-3 w-full">
                {canShowThumbs && showThumbs && (
                  <MiniThumbBar slides={slides} currentIndex={index} onSelect={handleSelectThumb} labels={labels} />
                )}

                <div className="w-full sm:w-auto sm:ml-auto">
                  <InfoCard
                    slide={slide}
                    meta={meta}
                    isOpen={infoOpen}
                    onToggle={handleToggleInfo}
                    canShowThumbs={canShowThumbs}
                    showThumbs={showThumbs}
                    onToggleThumbs={handleToggleThumbs}
                    onNavigate={onClose}
                    onAfterAction={refocusLightbox} // ✅ ключевая штука
                    labels={labels}
                  />
                </div>
              </div>
            </div>
          );
        },
      }}
    />
  );
}
